import { existsSync } from "node:fs";
import { readdir } from "node:fs/promises";
import { join } from "node:path";
import type { Command, CommandInput } from "@vortex-os/slash-commands";

/**
 * Output of {@link sessionStartCommand}'s handler. Pure data — the host
 * decides how to display it. Returning structured output rather than
 * printing keeps the command usable from non-CLI hosts (tests, web UIs).
 */
export interface SessionStartReport {
  readonly time: string;
  readonly repoRoot: string;
  readonly dataDir: string;
  readonly counts: Readonly<Record<string, number>>;
  readonly missing: readonly string[];
}

const COUNTED_DIRS: readonly string[] = ["_memory", "worklog", "decision-log"];

/**
 * `/session-start` — Emit a small report that anchors a new session.
 * No side effects: reads filesystem counts only.
 *
 * The host (e.g. Claude Code session loop) is expected to display the
 * report and decide what to do next. This command's job is to surface
 * the facts, not to take action.
 */
export const sessionStartCommand: Command<SessionStartReport> = {
  name: "session-start",
  description: "Emit a start-of-session report (time + data directory counts).",
  handler: async (input: CommandInput): Promise<SessionStartReport> => {
    const { dataDir, repoRoot } = input.context;
    const counts: Record<string, number> = {};
    const missing: string[] = [];

    for (const name of COUNTED_DIRS) {
      const dir = join(dataDir, name);
      if (!existsSync(dir)) {
        missing.push(name);
        counts[name] = 0;
        continue;
      }
      counts[name] = await countMarkdown(dir, name === "worklog");
    }

    return {
      time: new Date().toISOString(),
      repoRoot,
      dataDir,
      counts,
      missing,
    };
  },
};

async function countMarkdown(dir: string, recursive: boolean): Promise<number> {
  let total = 0;
  const entries = await readdir(dir, { withFileTypes: true });
  for (const e of entries) {
    if (e.isFile()) {
      if (!e.name.endsWith(".md")) continue;
      if (e.name === "README.md" || e.name === "_INDEX.md" || e.name === "MEMORY.md") {
        continue;
      }
      if (e.name.startsWith("_TEMPLATE")) continue;
      total++;
    } else if (e.isDirectory() && recursive) {
      if (e.name.startsWith(".") || e.name.startsWith("_")) continue;
      total += await countMarkdown(join(dir, e.name), recursive);
    }
  }
  return total;
}
